import { STABILITY } from "./constants.js";

/* ===========================================================================
   Hold-still gating — a port of cardvision/vision/stability.py.

   The live loop used to judge stillness with its own rules: one frame, a 28-pixel
   allowance, and a slot-by-slot corner comparison. All three were looser than the
   package's, and the difference is not cosmetic — recognising the first almost-still
   frame is how a motion-blurred warp reaches retrieval, and a blurred warp is a wrong
   answer delivered confidently. This is the same gate the desktop scanner runs.
   =========================================================================== */

/**
 * Largest corner displacement between two quads, ordering-invariant.
 *
 * Corner *slot* assignment comes from `orderCorners`, whose sectors flip at 45-degree
 * rotations: a card held at that angle with normal hand tremor gets its corners
 * relabelled between consecutive frames, and a slot-by-slot comparison then reads a
 * whole card-length of "motion" from a card that has not moved. The quads describe the
 * same physical corners under some cyclic shift, so the drift is the best over the four
 * shifts — `stability.quad_drift`.
 *
 * Note this is the *maximum* coordinate displacement, matching numpy's
 * `abs(...).max()`, not the mean Euclidean distance the page used to compute. The two
 * differ by a factor of roughly two on a card rotating in a hand, which is precisely
 * the case the tolerance has to discriminate.
 */
export function quadDrift(current, previous) {
  if (!current || !previous) return Infinity;
  let best = Infinity;
  for (let shift = 0; shift < 4; shift++) {
    let worst = 0;
    for (let corner = 0; corner < 4; corner++) {
      // np.roll(current, shift, axis=0)[corner] === current[(corner - shift) mod 4]
      const from = (((corner - shift) % 4) + 4) % 4;
      const dx = Math.abs(current[from * 2] - previous[corner * 2]);
      const dy = Math.abs(current[from * 2 + 1] - previous[corner * 2 + 1]);
      if (dx > worst) worst = dx;
      if (dy > worst) worst = dy;
    }
    if (worst < best) best = worst;
  }
  return best;
}

/**
 * How far this card's corners may drift and still count as holding still.
 *
 * The larger of the fixed pixel tolerance and a fraction of the card's own size: six
 * pixels is right for a card on a mat, but a card held up close fills half the frame
 * and normal hand tremor moves its corners further than that essentially forever.
 */
export function driftTolerance(area) {
  const relative = STABILITY.relativeTolerance * Math.sqrt(Math.max(area, 1));
  return Math.max(STABILITY.tolerance, relative);
}

/** Mean corner position of a flat 8-element quad. */
export function quadCentre(quad) {
  return [(quad[0] + quad[2] + quad[4] + quad[6]) / 4, (quad[1] + quad[3] + quad[5] + quad[7]) / 4];
}

/**
 * One card's hold-still state — `stability.StabilityGate`.
 *
 * Two ways to settle. Holding still for `STABILITY.frames` is the classic one. The
 * *motion* settle is for a card that never manages it — held in a hand, whose tremor
 * restarts the hold-still window forever: a card tracked continuously for
 * `STABILITY.motionSettleFrames` fires anyway, once per track.
 */
export class StabilityGate {
  constructor() {
    this.reset();
  }

  reset() {
    this.previous = null;
    this.held = 0;
    this.firedAlready = false;
    //: Frames the same physical card has been continuously in view, still or not.
    this.tracked = 0;
    this.motionFired = false;
  }

  /** Whether the card currently in view has already been scanned. */
  get fired() {
    return this.firedAlready || this.motionFired;
  }

  /** Feed one frame's detection; true on the frame a card settles. */
  update(detection) {
    if (!detection) {
      this.reset();
      return false;
    }
    const quad = Float32Array.from(detection.quad);
    if (!this.previous) {
      // First sighting: one frame held, no drift measured yet. That still satisfies a
      // threshold of 1, which is what a static rig or a directory of stills asks for.
      this.previous = quad;
      this.held = 1;
      this.firedAlready = false;
      this.tracked = 1;
      this.motionFired = false;
      return this.settled();
    }

    const [cx, cy] = quadCentre(quad);
    const [px, py] = quadCentre(this.previous);
    if (Math.hypot(cx - px, cy - py) > 0.5 * Math.sqrt(Math.max(detection.area, 1))) {
      // A different placement, not the tracked card moving: restart everything.
      this.tracked = 0;
      this.motionFired = false;
    }
    this.tracked += 1;

    const drift = quadDrift(quad, this.previous);
    this.previous = quad;
    if (drift > driftTolerance(detection.area)) {
      this.held = 1;
      this.firedAlready = false;
      return this.settled() || this.motionSettled();
    }
    this.held += 1;
    return this.settled() || this.motionSettled();
  }

  /** Whether this frame is the one the card settles on — true exactly once. */
  settled() {
    if (this.held >= STABILITY.frames && !this.firedAlready) {
      this.firedAlready = true;
      // A card that settles is scanned; the motion settle must not scan it again.
      this.motionFired = true;
      return true;
    }
    return false;
  }

  /** Whether continuous tracking alone earns the scan — also true exactly once. */
  motionSettled() {
    const frames = STABILITY.motionSettleFrames;
    if (frames <= 0 || this.motionFired || this.tracked < frames) return false;
    this.motionFired = true;
    // And the reverse: once scanned in motion, the eventual hold-still (the user
    // steadying the card they are already holding) must not scan it twice.
    this.firedAlready = true;
    return true;
  }
}
